import { FolderPlus, ListPlus, Plus, Search } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { can } from '@/features/auth/permissions'
import { useSession } from '@/features/auth/use-session'
import { ProjectForm } from '@/features/projects/components/ProjectForm'
import { Popover } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import { useCommandPalette } from '@/shared/stores/command-palette-store'

import { QuickTaskDialog } from './QuickTaskDialog'

/**
 * Buscar y crear, siempre a mano: la barra de arriba de la navegación.
 *
 * **Buscar es el mismo `⌘K`.** La barra no es un segundo buscador: lo abre. Así
 * no hay dos que puedan encontrar cosas distintas, y quien no conocía el atajo
 * lo descubre leyéndolo en la barra.
 *
 * **"+" crea lo que más se crea**: una tarea (sin ir al proyecto) o un
 * proyecto. El proyecto usa el mismo formulario que el botón del portafolio.
 */
export function QuickBar({ className }: { className?: string }) {
  const t = useT()
  const navigate = useNavigate()
  const { me } = useSession()
  const abrirBuscador = useCommandPalette((state) => state.setOpen)

  const mas = useRef<HTMLButtonElement>(null)
  const [menu, setMenu] = useState(false)
  const [tarea, setTarea] = useState(false)
  const [proyecto, setProyecto] = useState(false)
  const cerrarMenu = useCallback(() => setMenu(false), [])
  const cerrarTarea = useCallback(() => setTarea(false), [])

  const item =
    'flex w-full items-center gap-2 rounded-[var(--radius-control)] px-2 py-1.5 text-left text-xs text-text-1 hover:bg-surface-2'

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <button
        type="button"
        onClick={() => abrirBuscador(true)}
        aria-label={t('quick.searchLabel')}
        title={t('quick.searchLabel')}
        className="hairline flex h-7 min-w-0 flex-1 items-center gap-1.5 rounded-[var(--radius-control)] bg-bg px-2 text-left text-xs text-text-3 hover:text-text-2"
      >
        <Search size={12} aria-hidden="true" className="shrink-0" />
        <span className="min-w-0 flex-1 truncate">{t('quick.search')}</span>
        <kbd className="num shrink-0 text-2xs">⌘K</kbd>
      </button>

      <button
        ref={mas}
        type="button"
        aria-label={t('quick.add')}
        aria-haspopup="menu"
        aria-expanded={menu}
        title={t('quick.add')}
        onClick={() => setMenu((abierto) => !abierto)}
        className="hairline flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-bg text-text-2 hover:text-text-1"
      >
        <Plus size={14} aria-hidden="true" />
      </button>

      <Popover anchor={mas} open={menu} onClose={cerrarMenu} role="menu" className="w-[200px] p-1">
        <button
          type="button"
          role="menuitem"
          className={item}
          onClick={() => {
            setMenu(false)
            setTarea(true)
          }}
        >
          <ListPlus size={14} aria-hidden="true" className="text-text-3" />
          {t('quick.newTask')}
        </button>
        {can(me, 'project:create') ? (
          <button
            type="button"
            role="menuitem"
            className={item}
            onClick={() => {
              setMenu(false)
              setProyecto(true)
            }}
          >
            <FolderPlus size={14} aria-hidden="true" className="text-text-3" />
            {t('quick.newProject')}
          </button>
        ) : null}
      </Popover>

      <QuickTaskDialog open={tarea} onClose={cerrarTarea} />
      <ProjectForm
        open={proyecto}
        onClose={() => setProyecto(false)}
        onCreated={(id) => {
          setProyecto(false)
          void navigate(`/projects/${id}`)
        }}
      />
    </div>
  )
}
